import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Send, MessageCircle, Clock, CheckCircle, Circle } from 'lucide-react';
import { useSavChat } from '@/shared/hooks/useSavChat';
import { useChatPresence } from '@/shared/hooks/useChatPresence';
import { useAuth } from '@/app/providers';
import { ChatStatusColors, SenderType, ChatStatus, ChatReason } from '@/models/savChat';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';


interface CustomerSavChatProps {
  chatId: string;
  isOpen: boolean;
  onClose: () => void;
  orderReference: string;
}

export const CustomerSavChat: React.FC<CustomerSavChatProps> = ({
  chatId,
  isOpen,
  onClose,
  orderReference,
}) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const { chat, messages, loading, sendMessage, markAsRead, reopenChat } = useSavChat(chatId, SenderType.CUSTOMER);

  // Mémoriser le nom du client pour éviter les re-renders
  const customerName = useMemo(() => {
    return chat?.customerName || currentUser?.displayName || 'Client';
  }, [chat?.customerName, currentUser?.displayName]);

  const { otherUserPresence, setTyping } = useChatPresence(
    chatId,
    currentUser?.uid || null,
    customerName,
    'customer'
  );
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [reopenReason, setReopenReason] = useState<ChatReason>(ChatReason.OTHER);
  const [reopenDetails, setReopenDetails] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Marquer les messages comme lus quand on ouvre le chat
  useEffect(() => {
    if (isOpen && chatId) {
      markAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, chatId]); // Intentionnellement, on ne met pas markAsRead dans les dépendances

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      // Si on réouvre un chat fermé, mettre à jour les métadonnées d'abord
      if (chat?.status === ChatStatus.CLOSED) {
        await reopenChat(reopenReason, reopenDetails);
      }

      await sendMessage(newMessage);
      setNewMessage('');
      setReopenDetails(''); // Reset details after sending
      setTyping(false); // Stop typing indicator after sending
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
    } finally {
      setIsSending(false);
    }
  };

  // Ref pour le debounce du typing indicator
  const typingDebounceRef = useRef<NodeJS.Timeout>();

  const handleMessageChange = (value: string) => {
    setNewMessage(value);

    // Debounce typing indicator pour éviter trop d'appels
    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current);
    }

    if (value.trim().length > 0) {
      typingDebounceRef.current = setTimeout(() => {
        setTyping(true);
      }, 300); // 300ms debounce
    } else {
      setTyping(false);
    }
  };

  const getStatusIcon = (status: ChatStatus) => {
    switch (status) {
      case ChatStatus.OPEN:
        return <Clock className="h-4 w-4" />;
      case ChatStatus.IN_PROGRESS:
        return <MessageCircle className="h-4 w-4" />;
      case ChatStatus.CLOSED:
        return <CheckCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 bg-black/50"
      onClick={(e) => {
        // Fermer si on clique sur le fond (pas sur le modal lui-même)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-surface rounded-none sm:rounded-2xl shadow-2xl w-full h-full sm:max-w-2xl sm:h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-light">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-foreground">{t('admin.savChat.customer.title')}</h2>
              {chat && (
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${ChatStatusColors[chat.status].bg
                    } ${ChatStatusColors[chat.status].text}`}
                >
                  {getStatusIcon(chat.status)}
                  {t(`admin.savChat.status.${chat.status.toLowerCase()}`)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-foreground-secondary">
                {t('admin.savChat.admin.info.payment.order')} #{orderReference}
              </p>
              {otherUserPresence && (
                <>
                  <span className="text-foreground-secondary">•</span>
                  <div className="flex items-center gap-1.5">
                    <Circle
                      className={`h-2 w-2 ${otherUserPresence.isOnline
                        ? 'fill-green-500 text-green-500'
                        : 'fill-gray-400 text-gray-400'
                        }`}
                    />
                    <span className="text-xs text-foreground-secondary">
                      {otherUserPresence.isOnline ? t('admin.savChat.admin.online') : t('admin.savChat.admin.offline')}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-foreground-secondary" />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-4 bg-background-secondary/30"
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-8 h-8 border-3 border-border border-t-primary rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm text-foreground-secondary">{t('admin.savChat.admin.loading')}</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MessageCircle className="h-12 w-12 text-foreground-secondary/50 mx-auto mb-3" />
                <p className="text-foreground-secondary">{t('admin.savChat.noMessages')}</p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => {
                if (message.senderType === SenderType.SYSTEM) {
                  return (
                    <div key={message.id} className="flex justify-center my-4">
                      <div className="bg-background-secondary/50 px-4 py-2 rounded-lg max-w-[80%] text-center border border-border-light">
                        <p className="text-xs text-foreground-secondary whitespace-pre-wrap">
                          {message.message}
                        </p>
                      </div>
                    </div>
                  );
                }

                const isCustomer = message.senderType === SenderType.CUSTOMER;
                return (
                  <div
                    key={message.id}
                    className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${isCustomer
                        ? 'bg-primary text-white'
                        : 'bg-surface border border-border'
                        }`}
                    >
                      <p className={`text-sm ${isCustomer ? 'text-white' : 'text-foreground'}`}>
                        {message.message}
                      </p>
                      <div
                        className={`flex items-center gap-2 mt-1.5 text-xs ${isCustomer ? 'text-white/70' : 'text-foreground-secondary'
                          }`}
                      >
                        <span className="font-medium">{message.senderName}</span>
                        <span>•</span>
                        <span>
                          {format(message.createdAt, 'HH:mm', { locale: i18n.language === 'fr' ? fr : undefined })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Typing indicator */}
              {otherUserPresence?.isTyping && (
                <div className="flex justify-start">
                  <div className="max-w-[70%] rounded-2xl px-4 py-2.5 bg-surface border border-border">
                    <div className="flex items-center gap-1.5">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-foreground-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-foreground-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-foreground-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                      <span className="text-xs text-foreground-secondary ml-2">
                        {t('admin.savChat.admin.typing', { name: otherUserPresence.userName })}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-border-light bg-surface">
          {chat?.status === ChatStatus.CLOSED && (
            <div className="p-4 bg-background-secondary/30 border-b border-border-light space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-foreground-secondary" />
                <p className="text-xs font-medium text-foreground-secondary">
                  {t('admin.savChat.chatClosed')}. {t('admin.savChat.customer.reopenReasonPrefix')}
                </p>
              </div>

              <div className="grid gap-3">
                <select
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value as ChatReason)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isSending}
                >
                  {Object.values(ChatReason).map((value) => (
                    <option key={value} value={value}>
                      {t(`admin.savChat.reasons.${value}`)}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  value={reopenDetails}
                  onChange={(e) => setReopenDetails(e.target.value)}
                  placeholder={t('admin.savChat.reasonDetails')}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isSending}
                />
              </div>
            </div>
          )}

          <form onSubmit={handleSendMessage} className="p-4">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => handleMessageChange(e.target.value)}
                  placeholder={chat?.status === ChatStatus.CLOSED ? t('admin.savChat.customer.reopenPlaceholder') : t('admin.savChat.writeMessage')}
                  rows={2}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder-foreground-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                  disabled={isSending}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={!newMessage.trim() || isSending}
                className="px-4 py-2.5 bg-primary text-white font-medium rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                {isSending ? t('admin.savChat.admin.sending') : (chat?.status === ChatStatus.CLOSED ? t('admin.savChat.customer.reopenButton') : t('admin.savChat.sendMessage'))}
              </button>
            </div>
            <p className="text-xs text-foreground-secondary mt-2">
              {t('admin.savChat.customer.inputHelp')}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

